import React from 'react';
import {useQuery} from "@apollo/client";
import {useTranslation} from "react-i18next";
import {useFormikContext} from "formik";
import {Box, Button, Chip, Grid, IconButton, Stack, TextField, Tooltip} from "@mui/material";
import {Typography} from "@mui/joy";
import {AddRounded, DeleteRounded} from "@mui-symbols-material/w300";
import {HarmfulSubstanceCatalog} from "../../graphql/queries/harmfulSubstanceCatalog";
import {ContaminationDeclarationFormValues, SUBSTANCES_FIELD} from "../../utils/contaminationDeclaration/fields";
import {EMPTY_SUBSTANCE_ROW, SubstanceRow} from "../../utils/contaminationDeclaration/types";

interface CatalogEntry {
    key: string;
    formula?: string | null;
    name: string;
    searchTerm?: string | null;
    dangerClass?: string | null;
    risks?: string | null;
    firstAid?: string | null;
}

// The catalog's leading entry is "atmosphere only" — a different statement
// than "exposed to this gas", so it gets its own button rather than a chip.
const AIR_KEY = 'AIR_N2_PURGE';

export const catalogEntryToRow = (entry: CatalogEntry): SubstanceRow => ({
    chemical: entry.formula ? `${entry.formula} – ${entry.name}` : entry.name,
    dangerClass: entry.dangerClass ?? '',
    risks: entry.risks ?? '',
    firstAid: entry.firstAid ?? '',
});

const CELLS: Array<{key: keyof SubstanceRow; label: string; multiline?: boolean}> = [
    {key: 'chemical', label: 'Chemical name / symbol'},
    {key: 'dangerClass', label: 'Danger class'},
    {key: 'risks', label: 'Risks & precautions', multiline: true},
    {key: 'firstAid', label: 'First aid / action on contact', multiline: true},
];

/**
 * Editable harmful-substances table. Rows live in Formik (`values.substances`)
 * and are sent as a whole list on save; the catalog only prefills a row — the
 * catalog key is never stored, so later catalog edits leave declarations alone.
 *
 * Layout: substances as a divided list (no boxes), then the add actions and
 * the catalog quick-fill on a grey tile (the section-1 tile surface) so data,
 * actions and the free-text field below read as three distinct areas.
 */
const SubstancesTable = ({disabled}: {disabled?: boolean}) => {
    const {t} = useTranslation();
    const {values, setFieldValue} = useFormikContext<ContaminationDeclarationFormValues>();
    const rows = values.substances ?? [];

    const catalogQuery = useQuery(HarmfulSubstanceCatalog, {fetchPolicy: 'cache-first', skip: disabled});
    const catalog: CatalogEntry[] = (catalogQuery.data?.harmfulSubstanceCatalog ?? []).filter(Boolean);
    const airEntry = catalog.find((e) => e.key === AIR_KEY);
    const gases = catalog.filter((e) => e.key !== AIR_KEY);

    const setRows = (next: SubstanceRow[]) => setFieldValue(SUBSTANCES_FIELD, next);
    const addRow = (row: SubstanceRow) => setRows([...rows, row]);
    const updateCell = (i: number, key: keyof SubstanceRow, value: string) =>
        setRows(rows.map((row, idx) => (idx === i ? {...row, [key]: value} : row)));
    const removeRow = (i: number) => setRows(rows.filter((_, idx) => idx !== i));

    return (
        <Box data-testid="doc-substances">
            {rows.length === 0 ? (
                <Typography level="body-sm" sx={{color: 'text.tertiary', mb: disabled ? 0 : '16px'}}>
                    {t('No substances listed yet. Pick one from the catalog or add a row.')}
                </Typography>
            ) : (
                <Box sx={{display: 'flex', flexDirection: 'column', mb: disabled ? 0 : '20px'}}>
                    {rows.map((row, i) => (
                        <Box
                            key={i}
                            data-testid="doc-substance-row"
                            sx={{
                                py: '20px', borderBottom: '1px solid #eeeeee',
                                '&:first-of-type': {pt: 0},
                                '&:last-of-type': {borderBottom: 'none', pb: 0},
                            }}
                        >
                            <Box sx={{display: 'flex', alignItems: 'center', mb: '12px'}}>
                                <Typography level="bodyBold" sx={{flex: 1, color: '#333'}}>{t('Substance {{index}}', {index: i + 1})}</Typography>
                                {!disabled && (
                                    <Tooltip title={t('Remove')} disableInteractive>
                                        <IconButton size="small" onClick={() => removeRow(i)} aria-label={t('Remove')} data-testid="doc-substance-remove">
                                            <DeleteRounded fontSize="small"/>
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                            <Grid container rowSpacing={3} columnSpacing={2}>
                                {CELLS.map((cell) => (
                                    <Grid item xs={12} md={6} key={cell.key}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={cell.label}
                                            value={row[cell.key] ?? ''}
                                            onChange={(e) => updateCell(i, cell.key, e.target.value)}
                                            disabled={disabled}
                                            multiline={cell.multiline}
                                            minRows={cell.multiline ? 2 : undefined}
                                            InputLabelProps={{shrink: true}}
                                            inputProps={{'data-testid': `doc-substance-${cell.key}`}}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    ))}
                </Box>
            )}

            {!disabled && (
                <Box
                    data-testid="doc-substances-add"
                    sx={{p: '16px', backgroundColor: '#f4f4f4', borderRadius: '4px', display: 'flex', flexDirection: 'column', gap: '12px'}}
                >
                    <Typography level="bodyBold" sx={{color: '#333'}}>{t('Add substance')}</Typography>
                    <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AddRounded fontSize="small"/>}
                            onClick={() => addRow(airEntry ? catalogEntryToRow(airEntry) : {...EMPTY_SUBSTANCE_ROW, chemical: 'Air / N₂ purge (atmosphere only)'})}
                            data-testid="doc-substance-add-air"
                        >
                            {t('Insert "Air / N₂ purge" row')}
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AddRounded fontSize="small"/>}
                            onClick={() => addRow({...EMPTY_SUBSTANCE_ROW})}
                            data-testid="doc-substance-add-empty"
                        >
                            {t('Add empty row')}
                        </Button>
                    </Box>
                    {gases.length > 0 && (
                        <Box>
                            <Typography level="body-xs" sx={{color: 'text.tertiary', mb: '6px'}}>
                                {t('Quick-fill from the catalog (danger class, risks and first aid are prefilled and stay editable):')}
                            </Typography>
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                {gases.map((entry) => (
                                    // Non-interactive: an interactive tooltip popper sits over the
                                    // chips of the next row and swallows their hover/click.
                                    <Tooltip key={entry.key} title={entry.name} placement="top" disableInteractive>
                                        <Chip
                                            label={entry.formula || entry.name}
                                            variant="outlined"
                                            size="small"
                                            onClick={() => addRow(catalogEntryToRow(entry))}
                                            data-testid={`doc-substance-catalog-${entry.key}`}
                                            sx={{backgroundColor: '#fff'}}
                                        />
                                    </Tooltip>
                                ))}
                            </Stack>
                        </Box>
                    )}
                    {catalogQuery.error && (
                        <Typography level="body-xs" color="warning">{t('The substance catalog could not be loaded.')}</Typography>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default SubstancesTable;
